/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from "react";
import { UploadCloud, X, FileText } from "lucide-react";
// Reuse the shared lightbox (same component used by ViewReimbursement / InfoView).
import MediaPreview, { useMediaPreview, MediaItem } from "components/MediaPreview";

// Accepted item shapes across the inventory forms:
//  • File                                  → newly picked file (Add & Edit)
//  • string                                → a plain URL
//  • { url?, s3Url?, originalName? }        → an existing saved attachment (Edit)
type UploadItem = File | string | { url?: string; s3Url?: string; originalName?: string };

interface InventoryImageUploaderProps {
  items: UploadItem[];
  onAddFiles: (files: FileList) => void;
  onRemove: (index: number) => void;
  accept: string;
  helpText: string;
  maxFiles: number;
  label?: string;
  error?: string;
  disabled?: boolean;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const fileNameFromUrl = (url: string): string => {
  try {
    const clean = url.split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || "") || "Attachment";
  } catch {
    return "Attachment";
  }
};

const isImageLike = (name: string, mime?: string): boolean => {
  if (mime?.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|bmp|tiff?|jfif)(\?|$)/i.test(name);
};

const resolveUrl = (item: UploadItem): string => {
  if (typeof item === "string") return item;
  if (item instanceof File) return "";
  return item?.url || item?.s3Url || "";
};

/**
 * Enterprise-style image uploader for the inventory forms: a click/drag
 * dropzone, per-file cards (thumbnail + name + size + remove), and the shared
 * MediaPreview lightbox for previews. Admin-config allowed formats / size /
 * count come in via `accept`, `helpText` and `maxFiles`; validation stays with
 * the caller's `onAddFiles` handler.
 */
export const InventoryImageUploader: React.FC<InventoryImageUploaderProps> = ({
  items,
  onAddFiles,
  onRemove,
  accept,
  helpText,
  maxFiles,
  label = "Upload Images",
  error,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Display/preview URL per item. Object URLs are created for File items and
  // revoked when the set changes or the component unmounts (no leaks).
  const urls = useMemo(
    () => items.map((it) => (it instanceof File ? URL.createObjectURL(it) : resolveUrl(it))),
    [items],
  );
  useEffect(
    () => () => {
      items.forEach((it, i) => {
        if (it instanceof File && urls[i]) URL.revokeObjectURL(urls[i]);
      });
    },
    [urls], // eslint-disable-line
  );

  const mediaItems: MediaItem[] = items.map((it, i) => ({
    url: urls[i],
    name: it instanceof File ? it.name : fileNameFromUrl(urls[i]),
    mimeType: it instanceof File ? it.type : undefined,
  }));
  const { previewProps, open: openPreview } = useMediaPreview(mediaItems);

  const atLimit = items.length >= maxFiles;

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) onAddFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled || atLimit) return;
    if (e.dataTransfer.files?.length) onAddFiles(e.dataTransfer.files);
  };

  return (
    <div className="flex flex-col">
      <label className="block mb-1.5 text-sm font-medium text-gray-900 dark:text-white">
        {label}
        {maxFiles ? (
          <span className="text-gray-400 dark:text-gray-500 font-normal"> ({items.length}/{maxFiles})</span>
        ) : null}
        {/* Allowed formats / size inlined so it takes no extra vertical space */}
        <span className="ml-2 text-[11px] font-normal text-gray-400 dark:text-gray-500">{helpText}</span>
      </label>

      {/* Single compact row: small upload tile + selected thumbnails inline */}
      <div className="flex flex-wrap items-start gap-3">
        {!atLimit && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            disabled={disabled}
            title="Click to upload or drag & drop"
            className="w-20 h-20 shrink-0 flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 text-gray-400 cursor-pointer hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UploadCloud className="w-5 h-5" aria-hidden="true" />
            <span className="text-[10px] font-medium">Upload</span>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              multiple
              className="hidden"
              onChange={handlePick}
            />
          </button>
        )}

        {items.map((it, i) => {
          const name = it instanceof File ? it.name : fileNameFromUrl(urls[i]);
          const sizeLabel = it instanceof File ? formatSize(it.size) : null;
          const img = isImageLike(name, it instanceof File ? it.type : undefined);
          return (
            <div key={i} className="relative w-20">
              <button
                type="button"
                onClick={() => openPreview(i)}
                title={`Preview ${name}`}
                className="block w-20 h-20 rounded-md overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:ring-2 hover:ring-inset hover:ring-primary transition"
              >
                {img ? (
                  <img src={urls[i]} alt={name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <FileText size={18} />
                  </div>
                )}
              </button>

              <button
                type="button"
                onClick={() => onRemove(i)}
                title="Remove"
                className="absolute -top-2 -right-2 z-10 w-5 h-5 rounded-full flex items-center justify-center bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-red-500 hover:text-red-600 shadow-md transition-colors"
              >
                <X size={12} />
              </button>

              <p className="mt-0.5 w-20 truncate text-[10px] leading-tight text-gray-600 dark:text-gray-300" title={name}>
                {sizeLabel ? `${name} · ${sizeLabel}` : name}
              </p>
            </div>
          );
        })}
      </div>

      {/* Reserved error line (prevents layout jump on validation) */}
      <span className="block min-h-[1rem] mt-1 text-red-500 text-xs font-medium leading-tight">
        {error}
      </span>

      {/* Shared lightbox — image zoom / PDF / video (same as ViewReimbursement) */}
      <MediaPreview {...previewProps} />
    </div>
  );
};

export default InventoryImageUploader;
